import React, { useState, useCallback } from 'react'
import jwt_decode from 'jwt-decode'
import ModalActions from 'reducers/Modal'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'styled-components'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { MaterialIcon } from '@dsny/dsny-component-library'
import ContactModal from '../Modals/ContactModal/ContactModal'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'
import { ReleasesService } from 'services'
import useReleaseActions from '../../hooks/useReleaseActions'
import { Morevert } from '@dsny/dsny-component-library/icons'
import styles from '../../../../components/Dropdowns/Dropdowns.module.scss'
import { trackEvent } from 'utils/segmentService'
import { normalizeValue } from 'utils/analytics'
import useArchiveToFilter from 'utils/hooks/useArchiveToFilter'

const ActionsDropdownNew = ({ release, setRelease }) => {
  const { t } = useTranslation()
  const { enabled: archiveToFilterEnabled } = useArchiveToFilter()
  const theme = useTheme()
  const dispatch = useDispatch()
  const isMobile = useMobileMediaQuery()
  const { currentReleaseInformation } = useSelector((state) => state.releases)
  const { playlistAbstracts } = useSelector((state) => state.playList)
  const [isContactModalOpen, setIsContactModalOpen] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  // Share release validation logic (copied from ActionsSectionNew)
  let isAbleToShareRelease = false
  try {
    const token = localStorage.getItem('accessToken')
    const currentUser = jwt_decode(token)
    isAbleToShareRelease =
      currentUser?.verified && currentReleaseInformation?.allow_sharing
  } catch (e) {
    isAbleToShareRelease = false
  }

  const openShareReleaseModal = useCallback(() => {
    dispatch(ModalActions.toggleShareReleaseModal())
  }, [dispatch])

  // Use the custom hook for release actions
  const {
    handleFlag,
    handleArchive,
    handleAddToPlaylist,
    createNewPlaylist: createNewPlaylistHook
  } = useReleaseActions(
    release,
    setRelease,
    'release_information_actions_dropdown'
  )

  const openContactModal = () => {
    setIsContactModalOpen(true)
    try {
      ReleasesService.trackCommentingInit(currentReleaseInformation?.releaseid)
    } catch (error) {
      console.log('Track error', error)
    }
    trackEvent('Release Contact', {
      release_id: normalizeValue(currentReleaseInformation?.releaseid),
      artist: normalizeValue(currentReleaseInformation?.artist),
      title: normalizeValue(currentReleaseInformation?.title),
      source: 'release_information_actions_dropdown',
      type: 'contact'
    })
  }

  // Use custom hook for playlist operations - just use the hook's functions directly
  const createNewPlaylist = createNewPlaylistHook

  // Use the hook's function for adding to playlist
  const handleAddToPlaylistLocal = handleAddToPlaylist

  if (!currentReleaseInformation) {
    return null
  }

  return (
    <>
      <DropdownMenu.Root onOpenChange={setIsDropdownOpen}>
        <DropdownMenu.Trigger asChild>
          <button
            aria-label={t('general.more')}
            className={styles.ReleaseInfoActionsTrigger}
          >
            <Morevert width={24} height={24} color={theme.colors.white0} />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            style={{
              background: theme.colors.neutralW20,
              color: theme.colors.neutralW100
            }}
            className={`${styles.DropdownMenuContent} ${styles.ReleaseInfoActionsDropdown}`}
            sideOffset={5}
            avoidCollisions
          >
            {/* Share release option, first item */}
            {isAbleToShareRelease && (
              <DropdownMenu.Item
                className={`${styles.DropdownMenuItem} ${styles.ReleaseInfoActionsItem}`}
                onClick={openShareReleaseModal}
              >
                {/* You can use a MaterialIcon or another icon if desired */}
                {t('share-release.share-release')}
              </DropdownMenu.Item>
            )}
            {currentReleaseInformation?.commenting_enabled && (
              <DropdownMenu.Item
                className={`${styles.DropdownMenuItem} ${styles.ReleaseInfoActionsItem}`}
                onClick={openContactModal}
              >
                {t('release-info.contact')}
              </DropdownMenu.Item>
            )}

            {!(archiveToFilterEnabled && release?.archived) && (
              <DropdownMenu.Sub>
                <DropdownMenu.SubTrigger
                  className={`${styles.DropdownMenuItem} ${styles.ReleaseInfoActionsItem}`}
                >
                  {t('general.addto')}
                  <div className={styles.RightSlot}>
                    <MaterialIcon
                      name="KeyboardArrowRight"
                      color={theme.colors.neutralW100}
                    />
                  </div>
                </DropdownMenu.SubTrigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.SubContent
                    style={{
                      background: theme.colors.neutralW20,
                      color: theme.colors.neutralW100
                    }}
                    className={`${styles.DropdownMenuContent} ${styles.ReleaseInfoActionsDropdown}`}
                    sideOffset={2}
                    alignOffset={-5}
                  >
                    {playlistAbstracts?.map((playlist) => {
                      return (
                        <DropdownMenu.Item
                          key={playlist?.playlistid}
                          className={`${styles.DropdownMenuItem} ${styles.ReleaseInfoActionsItem}`}
                          onClick={() => handleAddToPlaylistLocal(playlist)}
                        >
                          {playlist?.name}
                        </DropdownMenu.Item>
                      )
                    })}

                    <DropdownMenu.Item
                      className={`${styles.DropdownMenuItem} ${styles.ReleaseInfoActionsItem}`}
                      onClick={createNewPlaylist}
                    >
                      New playlist{' '}
                    </DropdownMenu.Item>
                  </DropdownMenu.SubContent>
                </DropdownMenu.Portal>
              </DropdownMenu.Sub>
            )}

            <DropdownMenu.Item
              className={`${styles.DropdownMenuItem} ${styles.ReleaseInfoActionsItem}`}
              onClick={handleFlag}
            >
              {release?.flagged ? t('general.unflag') : t('general.flag')}
            </DropdownMenu.Item>

            {!isMobile && (
              <DropdownMenu.Item
                className={`${styles.DropdownMenuItem} ${styles.ReleaseInfoActionsItem}`}
                onClick={handleArchive}
              >
                {release?.archived
                  ? t('general.unarchive')
                  : t('general.archive')}
              </DropdownMenu.Item>
            )}

            <DropdownMenu.Arrow
              className={styles.DropdownMenuArrow}
              style={{ fill: theme.colors.neutralW20 }}
            />
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <ContactModal
        isContactModalOpen={isContactModalOpen}
        setIsContactModalOpen={setIsContactModalOpen}
      />
    </>
  )
}

export default ActionsDropdownNew
